
import Vue from 'vue';
import '@/assets/css/helpdesk/helpdesk-theme.css';
import Component from 'vue-class-component';
import {Watch} from 'vue-property-decorator';
import {namespace} from 'vuex-class';
import PaginationBar from '@/components/shared/PaginationBar.vue';
import RichTextEditor from '@/components/helpdesk/RichTextEditor.vue';
import EmailHtmlFrame from '@/components/helpdesk/EmailHtmlFrame.vue';
import BrokenImagesNotice from '@/components/helpdesk/BrokenImagesNotice.vue';
import {IEmailTemplate} from '@/store/modules/helpdeskEmailTemplatesModule';

const mod = namespace('helpdeskEmailTemplatesModule');

@Component({name: 'HelpdeskEmailTemplates', components: {PaginationBar, RichTextEditor, EmailHtmlFrame, BrokenImagesNotice}})
export default class HelpdeskEmailTemplates extends Vue {
  @mod.Getter('templates')    private templates!: IEmailTemplate[];
  @mod.Getter('currentPage')  private currentPage!: number;
  @mod.Getter('lastPage')     private lastPage!: number;
  @mod.Getter('total')        private total!: number;
  @mod.Getter('perPage')      private perPage!: number;

  private dialog          = false;
  private confirmDelete   = false;
  private saving          = false;
  private valid           = false;
  private editTarget: IEmailTemplate | null = null;
  private deleteTarget: IEmailTemplate | null = null;
  private selectedPerPage = 12;
  private search          = '';
  private searchTimer: any = null;
  private subscribes: any = {};
  private previewOpen     = false;
  private previewTarget: IEmailTemplate | null = null;
  private togglingId: number | null   = null;
  private duplicatingId: number | null = null;
  // Debounced copy of form.body: re-rendering the preview frame on every keystroke would flicker.
  private previewBody     = '';
  private previewTimer: any = null;

  private form = {
    name:      '',
    subject:   '',
    body:      '',
    is_active: true,
  };

  created() {
    this.subscribes = (this as any).$ons({
      'backend.call.OK.templateCreated': () => { this.saving = false; this.duplicatingId = null; this.dialog = false; this.loadData(); },
      'backend.call.KO.templateCreated': () => { this.saving = false; this.duplicatingId = null; },
      'backend.call.OK.templateUpdated': () => { this.saving = false; this.togglingId = null; this.dialog = false; },
      'backend.call.KO.templateUpdated': () => { this.saving = false; this.togglingId = null; },
      'backend.call.OK.templateDeleted': () => { this.loadData(); },
    });
    this.loadData();
  }

  beforeDestroy() { (this as any).$offs(this.subscribes); }

  loadData() {
    const params: any = {page: this.currentPage, per_page: this.selectedPerPage};
    if (this.search) params.search = this.search;
    this.$store.dispatch('helpdeskEmailTemplatesModule/fetchTemplates', params);
  }

  private onPageChange(page: number) {
    const params: any = {page, per_page: this.selectedPerPage};
    if (this.search) params.search = this.search;
    this.$store.dispatch('helpdeskEmailTemplatesModule/fetchTemplates', params);
  }

  private onSearchInput() {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.loadData(), 400);
  }

  private onSearchClear() {
    this.search = '';
    this.loadData();
  }

  private onPerPageChange(value: number) {
    this.selectedPerPage = value;
    this.loadData();
  }

  private openCreate() {
    this.editTarget = null;
    this.form = {name: '', subject: '', body: '', is_active: true};
    this.saving = false;
    this.dialog = true;
  }

  private openEdit(item: IEmailTemplate) {
    this.editTarget = item;
    this.form = {
      name:      item.name,
      subject:   item.subject || '',
      body:      item.body,
      is_active: item.is_active,
    };
    this.saving = false;
    this.dialog = true;
  }

  private onSave() {
    const formRef = this.$refs.form as any;
    if (!formRef.validate()) return;
    this.saving = true;
    const data: any = {
      name:      this.form.name,
      body:      this.form.body,
      is_active: this.form.is_active,
      subject:   this.form.subject || null,
    };
    if (this.editTarget) {
      this.$store.dispatch('helpdeskEmailTemplatesModule/updateTemplate', {id: this.editTarget.id, data});
    } else {
      this.$store.dispatch('helpdeskEmailTemplatesModule/createTemplate', data);
    }
  }

  @Watch('form.body', {immediate: true})
  private onBodyChanged(body: string) {
    clearTimeout(this.previewTimer);
    this.previewTimer = setTimeout(() => { this.previewBody = body || ''; }, 300);
  }

  private openPreview(item: IEmailTemplate) {
    this.previewTarget = item;
    this.previewOpen   = true;
  }

  // Quick on/off from the card: inactive templates are hidden from the reply pickers.
  private toggleActive(item: IEmailTemplate, active: boolean) {
    if (this.togglingId !== null) return;
    this.togglingId = item.id;
    this.$store.dispatch('helpdeskEmailTemplatesModule/updateTemplate', {id: item.id, data: {is_active: !!active}});
  }

  // Starting from an existing template is the fastest way to write a variant.
  private duplicate(item: IEmailTemplate) {
    if (this.duplicatingId !== null) return;
    this.duplicatingId = item.id;
    this.$store.dispatch('helpdeskEmailTemplatesModule/createTemplate', {
      name:      'Copia de ' + item.name,
      subject:   item.subject || null,
      body:      item.body,
      is_active: item.is_active,
    });
  }

  private relativeDate(date: string): string {
    if (!date) return '—';
    const d = new Date(date);
    const days = Math.floor((new Date().setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86400000);
    if (days <= 0) return 'hoy';
    if (days === 1) return 'ayer';
    if (days < 7) return 'hace ' + days + ' días';
    return 'el ' + d.toLocaleDateString('es-ES', {day: 'numeric', month: 'short', year: 'numeric'});
  }

  private onDelete(item: IEmailTemplate) {
    this.deleteTarget = item;
    this.confirmDelete = true;
  }

  private doDelete() {
    if (!this.deleteTarget) return;
    this.confirmDelete = false;
    this.$store.dispatch('helpdeskEmailTemplatesModule/deleteTemplate', this.deleteTarget.id);
  }

}
